'use client';

import React, { Fragment, useContext } from 'react';
import { useBloodVision } from '../BloodVisionContext';
import { PrimaryButton } from 'components/shared/PrimaryButton';
import {
  MidPanel,
  MidPanelPhone,
  RightPanel,
} from 'components/shared/BottomBar';
import { ZipCodeContext } from './ZipCodeContext';
import { DotSpinner } from 'components/shared';
import { useBvFreeCampaignModal } from './BvFreeCampaignModal';
import {
  getFirstIncompleteCheckoutStep,
  scrollToCheckoutStep,
} from './checkoutGuidance';
import styles from './BloodVisionBottomBar.module.css';

const BloodVisionBottomBar: React.FC = () => {
  const {
    checkoutAllowed,
    selectedPlan,
    hasUserSelectedPlan,
    total,
    handleCheckout,
    isLoading,
    isCountrySupported,
    zipCode,
    hasCheckedOut,
    setHasCheckedOut,
    isZipValid,
    selectedModality,
    isHsaFsaIncluded,
    plans,
    uiComponents,
    showSelectionRequiredHint,
  } = useBloodVision();

  // Get refs from context
  const { zipCodeInputRef, modalitySectionRef, hsaSectionRef } =
    useContext(ZipCodeContext);

  // Check if plan has is_plan_campaign: true
  const isCampaignActive = React.useMemo(() => {
    if (!selectedPlan) return false;
    return selectedPlan.is_plan_campaign === true;
  }, [selectedPlan]);

  // Use the campaign modal hook
  const { showCampaignModal } = useBvFreeCampaignModal();

  // For the checkout button, use isValidUSZip which checks if it's a valid US ZIP
  const isButtonDisabled = isLoading || !checkoutAllowed || hasCheckedOut;
  const sctollToOrderSummary = () => {
    const ref = document.getElementById('order-summary-bv-card');
    if (ref) ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleWrapperClick = () => {
    if (!isButtonDisabled) return;

    const missingStep = getFirstIncompleteCheckoutStep({
      zipCode,
      isZipValid,
      selectedModality,
      isHsaFsaIncluded,
      uiComponents,
      isCampaignActive,
    });

    scrollToCheckoutStep(missingStep, {
      zipCodeInputRef,
      modalitySectionRef,
      hsaSectionRef,
      showSelectionRequiredHint,
    });
  };

  // Handler for button click that disables the button and calls handleCheckout
  const handleButtonClick = () => {
    if (isCampaignActive) {
      // Show the campaign modal instead of proceeding to checkout
      // Don't set hasCheckedOut for campaign plans to allow re-clicking
      showCampaignModal();
    } else {
      // Normal checkout flow - only disable button for non-campaign plans
      setHasCheckedOut(true);
      handleCheckout();
    }
  };

  if (!isCountrySupported || !selectedPlan || !hasUserSelectedPlan) {
    return null;
  }

  const priceComponent =
    !isLoading && selectedPlan ? (
      <Fragment>
        <div className={styles.priceContainer}>
          <div className={styles.element}>
            ${total?.toFixed(0).toLocaleString()}{' '}
          </div>
          {/* Per month bellow */}
          <div className={`${styles.element} ${styles.secondary}`}>
            {total !== null && total > 0 ? (
              <>
                {selectedPlan.id === plans[1].id
                  ? 'Renews annually'
                  : 'Renews half-yearly'}{' '}
                {/* ${total?.toFixed(0).toLocaleString()} */}
              </>
            ) : (
              'Blood Vision campaign discount applied'
            )}
          </div>
        </div>
      </Fragment>
    ) : (
      <DotSpinner color="rgba(0,0,0,0.5)" style={{ paddingLeft: '8px' }} />
    );

  return (
    <div className={`${styles.bottomBarContainer} u-grid`}>
      <MidPanelPhone onClick={sctollToOrderSummary}>
        <div className={styles.price}>
          <div className={styles.subtitleTextBtn}>Total</div>
          {Number.isInteger(total) ? (
            <div className={styles.totalPriceSingle}>
              {/* {showStrikedPrice ? (
                <span className="original-price">
                  ${selectedPlan?.original_price}{' '}
                </span>
              ) : null} */}
              {priceComponent}
              {/* <span className="tax-disclaimer">(Taxes incl.)</span> */}
            </div>
          ) : null}
        </div>
      </MidPanelPhone>

      <MidPanel>
        <div className={styles.priceContainer}>
          {Number.isInteger(total) ? (
            <div className={styles.price}>
              {/* <span className="original-price">${originalTotal.toLocaleString()}</span> */}
              {/* {showStrikedPrice ? (
                <span className="original-price">
                  ${selectedPlan?.original_price}
                </span>
              ) : null} */}
              <span className={styles.currentPrice}>{priceComponent}</span>
              {/* <span className="tax-disclaimer">(Taxes incl.)</span> */}
            </div>
          ) : null}
        </div>
      </MidPanel>

      {/* CTA here */}
      <RightPanel>
        {/* Pass the disabled state to the wrapper so it only catches clicks when the button is disabled */}
        <div
          className={styles.buttonWrapper}
          onClick={handleWrapperClick}
          data-disabled={isButtonDisabled}
        >
          <PrimaryButton
            onClick={handleButtonClick}
            disabled={isButtonDisabled}
          >
            Proceed to pay
            {/* {checkoutAllowed ? 'CA' : null}{' '}
            {isLoading ? 'L' : null} */}
          </PrimaryButton>
        </div>
      </RightPanel>
    </div>
  );
};

export default BloodVisionBottomBar;
